// Color customization interface
export interface ColorCustomization {
  mapColor: string;      // Hex code for map/terrain color
  baseBoardColor: string; // Hex code for baseboard color
  textColor: string;     // Hex code for text color
}

// Color palette interface
export interface ColorPalette {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  colors: ColorCustomization;
  renderImage?: string;  // Base filename without extension (e.g., '05-sage-cream')
  previewImage?: string;
  isDefault?: boolean;
}

// Size configuration with proportional layout dimensions
export interface SizeConfig {
  id: string;
  name: string;
  displayName: string;
  dimensions: string;
  baseboardCm: { width: number; height: number };
  totalSizeWithFrameCm: { width: number; height: number };
  frameCm: number;
  marginsCm: number;
  mapAreaCm: { width: number; height: number };
  textAreaCm: { width: number; height: number };
  mapToTextSpaceCm: number;
  defaultRadius?: number; // in km
}

// Defines the structure for our configuration
export interface CustomizerConfig {
  tabs: {
    map: boolean;
    labels: boolean;
    style: boolean;
    palette?: boolean; // New palette tab
    size: boolean;
    frames: boolean;
  };
  defaultLocation?: {
    lat: number;
    lng: number;
    zoom: number;
    address: string;
  };
  styles: Array<{
    id: string;
    name: string;
    preview: string;
    mapboxStyleUrl: string; // Added for OSM-based styles
    colors: {
      land: string;
      water: string;
      roads: string;
      buildings: string;
    };
  }>;
  colorPalettes?: ColorPalette[]; // New color palettes
  sizes: Array<SizeConfig | {
    id: string;
    name: string;
    dimensions: string;
    defaultRadius: number; // in km
  }>;
  frames: Array<{
    id: string;
    name: string;
    color: string;
    material: string;
  }>;
}

// Configuration for the 3D Map Art product
export const mapArtConfig: CustomizerConfig = {
  tabs: {
    map: true,
    labels: true,
    style: true,
    palette: true, // Enable palette tab
    size: true,
    frames: true,
  },
  defaultLocation: {
    lat: 34.0522,
    lng: -118.2437,
    zoom: 15, // Increased zoom to show buildings
    address: 'Downtown Los Angeles, CA',
  },
  styles: [
    {
      id: 'osm2world',
      name: 'Modern',
      preview: '/styles/modern.jpg',
      mapboxStyleUrl: 'mapbox://styles/mapbox/light-v11',
      colors: {
        land: '#f0f0f0',
        water: '#4a90e2',
        roads: '#333333',
        buildings: '#cccccc',
      },
    },
    {
      id: 'dark',
      name: 'Dark',
      preview: '/styles/dark.jpg',
      mapboxStyleUrl: 'mapbox://styles/mapbox/dark-v11',
      colors: {
        land: '#1a1a1a',
        water: '#0a3d62',
        roads: '#ffffff',
        buildings: '#333333',
      },
    },
    {
      id: 'asphalt',
      name: 'Asphalt',
      preview: '/styles/asphalt.jpg',
      mapboxStyleUrl: 'mapbox://styles/mapbox/navigation-night-v1',
      colors: {
        land: '#2c2c2c',
        water: '#1e3a5f',
        roads: '#4a4a4a',
        buildings: '#3e3e3e',
      },
    },
    {
      id: 'sky',
      name: 'Sky',
      preview: '/styles/sky.jpg',
      mapboxStyleUrl: 'mapbox://styles/mapbox/outdoors-v12',
      colors: {
        land: '#e8f4f8',
        water: '#87ceeb',
        roads: '#5c9ead',
        buildings: '#c4dfe6',
      },
    },
    {
      id: 'nautical',
      name: 'Nautical',
      preview: '/styles/nautical.jpg',
      mapboxStyleUrl: 'mapbox://styles/mapbox/streets-v12',
      colors: {
        land: '#f5f5dc',
        water: '#006994',
        roads: '#4b5d67',
        buildings: '#d4d4aa',
      },
    },
    {
      id: 'minimalist',
      name: 'Minimalist',
      preview: '/styles/minimalist.jpg',
      mapboxStyleUrl: 'mapbox://styles/mapbox/light-v11',
      colors: {
        land: '#ffffff',
        water: '#000000',
        roads: '#666666',
        buildings: '#e0e0e0',
      },
    },
  ],
  // Color palettes fetched dynamically from API via paletteService.ts.
  // See useMapStore.palettes for runtime palette data.
  sizes: [
    {
      id: 'xs',
      name: 'XS',
      displayName: 'XS (Test Size)',
      dimensions: '4" x 5"',
      baseboardCm: { width: 10.2, height: 12.7 },
      totalSizeWithFrameCm: { width: 12.8, height: 15.3 },
      frameCm: 1.3,
      marginsCm: 1.0,
      mapAreaCm: { width: 8.2, height: 8.2 },
      textAreaCm: { width: 8.2, height: 2.3 },
      mapToTextSpaceCm: 0.2,
      defaultRadius: 1.5,
    },
    {
      id: 'small',
      name: 'Small',
      displayName: 'Small',
      dimensions: '8" x 10"',
      baseboardCm: { width: 20.4, height: 25.4 },
      totalSizeWithFrameCm: { width: 23.0, height: 28.0 },
      frameCm: 1.3,
      marginsCm: 2.0,
      mapAreaCm: { width: 16.4, height: 16.4 },
      textAreaCm: { width: 16.4, height: 4.6 },
      mapToTextSpaceCm: 0.4,
      defaultRadius: 3.0,
    },
    {
      id: 'medium',
      name: 'Medium',
      displayName: 'Medium',
      dimensions: '12" x 15"',
      baseboardCm: { width: 30.6, height: 38.1 },
      totalSizeWithFrameCm: { width: 33.2, height: 40.7 },
      frameCm: 1.3,
      marginsCm: 3.0,
      mapAreaCm: { width: 24.6, height: 24.6 },
      textAreaCm: { width: 24.6, height: 6.9 },
      mapToTextSpaceCm: 0.6,
      defaultRadius: 6.0,
    },
    {
      id: 'large',
      name: 'Large',
      displayName: 'Large',
      dimensions: '16" x 20"',
      baseboardCm: { width: 40.8, height: 50.8 },
      totalSizeWithFrameCm: { width: 43.4, height: 53.4 },
      frameCm: 1.3,
      marginsCm: 4.0,
      mapAreaCm: { width: 32.8, height: 32.8 },
      textAreaCm: { width: 32.8, height: 9.2 },
      mapToTextSpaceCm: 0.8,
      defaultRadius: 10.0,
    },
  ],
  frames: [
    {
      id: 'none',
      name: 'None',
      color: 'transparent',
      material: 'No Frame',
    },
    {
      id: 'black',
      name: 'Black',
      color: '#000000',
      material: 'Matte Black Wood',
    },
    {
      id: 'white',
      name: 'White',
      color: '#ffffff',
      material: 'Satin White Wood',
    },
    {
      id: 'natural',
      name: 'Natural',
      color: '#8b6f47',
      material: 'Natural Oak Wood',
    },
    {
      id: 'walnut',
      name: 'Walnut',
      color: '#5d4e37',
      material: 'Dark Walnut Wood',
    },
  ],
};

// Lookup maps: translate between config IDs and Shopify variant display values
// Shopify stores option values as letter x with no spaces: 8"x10"
export const sizeDisplayToId: Record<string, string> = {
  '4"x5"': 'xs',     '4" x 5"': 'xs',    '4"×5"': 'xs',
  '8"x10"': 'small',  '8" x 10"': 'small', '8"×10"': 'small',
  '12"x15"': 'medium', '12" x 15"': 'medium', '12"×15"': 'medium',
  '16"x20"': 'large',  '16" x 20"': 'large', '16"×20"': 'large',
};

export const sizeIdToDisplay: Record<string, string> = {
  'xs': '4"x5"',
  'small': '8"x10"',
  'medium': '12"x15"',
  'large': '16"x20"',
};

// Frame lookups: selectedOptions.Frame stores frame.name (capitalized display values)
// frameDisplayToId maps Shopify display → frame.name (NOT frame.id)
// because FramesTab, PreviewArea, SimplePreview all compare against frame.name
export const frameDisplayToId: Record<string, string> = {
  'No Frame': 'None',
  'None': 'None',
  'Black': 'Black',
  'White': 'White',
  'Natural': 'Natural',
  'Walnut': 'Walnut',
};

export const frameIdToDisplay: Record<string, string> = {
  'None': 'No Frame',
  'No Frame': 'No Frame',
  'Black': 'Black',
  'White': 'White',
  'Natural': 'Natural',
  'Walnut': 'Walnut',
};

// Example for a future, simpler product that reuses the component
export const starChartConfig: CustomizerConfig = {
  tabs: {
    map: false, // Map tab is disabled
    labels: true,
    style: true,
    size: true,
    frames: false, // Frames tab is disabled
  },
  styles: [
    {
      id: 'night',
      name: 'Night Sky',
      preview: '/styles/night.jpg',
      colors: {
        land: '#000033',
        water: '#000066',
        roads: '#ffffff',
        buildings: '#333366',
      },
    },
  ],
  sizes: [
    {
      id: 'standard',
      name: 'Standard',
      dimensions: '12×12 in',
      defaultRadius: 10,
    },
  ],
  frames: [],
};